if (!customElements.get('bundle-offer')) {
  customElements.define(
    'bundle-offer',
    class BundleOffer extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        if (this.dataset.mode === 'selector') {
          this.onFormSubmit = this.onFormSubmit.bind(this);
          this.onQuantityChange = this.onQuantityChange.bind(this);
          // Capture phase: runs before any add-to-cart handler, so the form is always up to date.
          document.addEventListener('submit', this.onFormSubmit, true);
          document.addEventListener('change', this.onQuantityChange);
          this.addEventListener('change', () => this.updateSelector());
          this.updateSelector();
          // Dawn redraws the Add to cart button after a variant change; re-apply our state.
          if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
            this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => {
              this.originalButton = null;
              setTimeout(() => this.updateSelector());
            });
          }
        } else {
          this.button = this.querySelector('[data-bundle-add]');
          this.label = this.querySelector('.bundle-summary__label');
          this.priceEl = this.querySelector('.bundle-summary__price');
          this.compareEl = this.querySelector('.bundle-summary__compare');
          this.saveEl = this.querySelector('.bundle-summary__save');
          this.errorEl = this.querySelector('.bundle-summary__error');
          this.initialPrice = this.priceEl ? this.priceEl.innerHTML : '';
          this.querySelectorAll('.bundle-card__check').forEach((input) =>
            input.addEventListener('change', () => this.updateBanner())
          );
          if (this.button) this.button.addEventListener('click', () => this.onBannerAdd());
        }
      }

      disconnectedCallback() {
        if (this.onFormSubmit) document.removeEventListener('submit', this.onFormSubmit, true);
        if (this.onQuantityChange) document.removeEventListener('change', this.onQuantityChange);
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        this.initialized = false;
      }

      /* ---------- Selector layout (inside product info) ---------- */

      get form() {
        return document.getElementById(this.dataset.formId);
      }

      get selectedInput() {
        return this.querySelector('.bundle-option__input:checked');
      }

      updateSelector() {
        const selected = this.selectedInput;
        this.querySelectorAll('.bundle-option').forEach((label) => {
          label.classList.toggle('is-selected', label.contains(selected));
        });

        this.syncForm();

        const form = this.form;
        if (!form) return;
        const submit = form.querySelector('[type="submit"]');
        const text = submit && submit.querySelector('span');
        if (!submit || !text) return;

        if (!this.originalButton) {
          this.originalButton = {
            text: text.textContent,
            disabled: submit.hasAttribute('disabled'),
          };
        }

        if (this.usesBundleItems) {
          submit.removeAttribute('disabled');
          submit.removeAttribute('aria-disabled');
          text.textContent =
            selected.dataset.isDuo === 'true' && this.dataset.duoLabel
              ? this.dataset.duoLabel
              : window.variantStrings.addToCart;
        } else {
          text.textContent = this.originalButton.text;
          if (this.originalButton.disabled) submit.setAttribute('disabled', 'disabled');
        }
      }

      get usesBundleItems() {
        const selected = this.selectedInput;
        return Boolean(selected && selected.dataset.current !== 'true');
      }

      getQuantity(form) {
        const input =
          form.querySelector('[name="quantity"]') || document.querySelector(`[name="quantity"][form="${form.id}"]`);
        return Math.max(parseInt(input && input.value, 10) || 1, 1);
      }

      /*
        Puts the selected items straight into the product form as items[n][id] / items[n][quantity].
        The theme's normal Add to cart then sends every item (e.g. Spray + Cream) in one request.
      */
      syncForm() {
        const form = this.form;
        if (!form) return;

        form.querySelectorAll('.bundle-offer__item-input').forEach((input) => input.remove());
        const idInput = form.querySelector('[name="id"]');

        if (!this.usesBundleItems) {
          if (idInput && idInput.dataset.bundleWasDisabled !== undefined) {
            idInput.disabled = idInput.dataset.bundleWasDisabled === 'true';
            delete idInput.dataset.bundleWasDisabled;
          }
          return;
        }

        if (idInput) {
          if (idInput.dataset.bundleWasDisabled === undefined) {
            idInput.dataset.bundleWasDisabled = String(idInput.disabled);
          }
          idInput.disabled = true;
        }

        const quantity = this.getQuantity(form);
        JSON.parse(this.selectedInput.dataset.items).forEach((item, index) => {
          [
            [`items[${index}][id]`, item.id],
            [`items[${index}][quantity]`, quantity],
          ].forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            input.className = 'bundle-offer__item-input';
            form.appendChild(input);
          });
        });
      }

      onFormSubmit(evt) {
        if (!this.isConnected || evt.target.id !== this.dataset.formId) return;
        this.syncForm();
      }

      onQuantityChange(evt) {
        if (evt.target && evt.target.name === 'quantity') this.syncForm();
      }

      /* ---------- Banner layout (Better Together) ---------- */

      get checkedInputs() {
        return Array.from(this.querySelectorAll('.bundle-card__check:checked'));
      }

      updateBanner() {
        const all = this.querySelectorAll('.bundle-card__check:not(:disabled)');
        const checked = this.checkedInputs;
        const isDuo = checked.length === all.length && all.length > 1;

        this.classList.toggle('is-partial', !isDuo);
        if (this.compareEl) this.compareEl.hidden = !isDuo;
        if (this.saveEl) this.saveEl.hidden = !isDuo;

        if (this.priceEl) {
          if (isDuo) {
            this.priceEl.innerHTML = this.initialPrice;
          } else {
            const total = checked.reduce((sum, input) => sum + parseInt(input.dataset.price, 10), 0);
            const card = checked[0] && checked[0].closest('.bundle-card');
            this.priceEl.innerHTML =
              checked.length === 1 && card ? card.querySelector('.bundle-card__price').innerHTML : this.formatMoney(total);
          }
        }

        if (this.button) {
          this.button.disabled = checked.length === 0;
          this.label.textContent = isDuo ? this.dataset.duoLabel : this.dataset.singleLabel;
        }
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '{{amount}}';
        const value = cents / 100;
        const withSeparators = (num, decimals, thousands, decimal) => {
          const [whole, fraction] = num.toFixed(decimals).split('.');
          const out = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? out + decimal + fraction : out;
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_no_decimals':
              return withSeparators(value, 0, ',', '.');
            case 'amount_with_comma_separator':
              return withSeparators(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withSeparators(value, 0, '.', ',');
            default:
              return Number.isInteger(value) ? withSeparators(value, 0, ',', '.') : withSeparators(value, 2, ',', '.');
          }
        });
      }

      onBannerAdd() {
        const all = this.querySelectorAll('.bundle-card__check:not(:disabled)');
        const checked = this.checkedInputs;
        if (!checked.length) return;

        let items;
        if (checked.length === all.length && all.length > 1) {
          items = JSON.parse(this.dataset.duoItems).map((item) => ({ id: item.id, quantity: 1 }));
        } else {
          items = checked.map((input) => ({ id: parseInt(input.value, 10), quantity: 1 }));
        }

        this.errorEl.hidden = true;
        this.addToCart(items, this.button, (message) => {
          this.errorEl.textContent = message;
          this.errorEl.hidden = false;
        });
      }

      /* ---------- Shared add to cart ---------- */

      addToCart(items, button, onError) {
        if (!button || button.getAttribute('aria-disabled') === 'true') return;
        const spinner = button.querySelector('.loading__spinner');
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');

        const body = { items };
        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('json');
        config.body = JSON.stringify(body);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              onError(response.description || response.message);
              return;
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const firstItem = (response.items && response.items[0]) || response;
            const state = Object.assign({}, firstItem, { sections: response.sections });

            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'bundle-offer',
                productVariantId: firstItem.variant_id,
                cartData: state,
              });
            }
            this.cart.renderContents(state);
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          })
          .catch((error) => {
            console.error(error);
            onError(window.cartStrings ? window.cartStrings.error : 'Something went wrong');
          })
          .finally(() => {
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          });
      }
    }
  );
}
